import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Star, Gem } from "lucide-react";
import { http } from "@/lib/api";
import Sparkles from "@/components/Sparkles";
import HeroReveal from "@/components/HeroReveal";

export default function Home() {
  const [site, setSite] = useState(null);
  const [rates, setRates] = useState([]);
  const [media, setMedia] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, m] = await Promise.all([
          http.get("/site"),
          http.get("/rates"),
          http.get("/media?type=photo"),
        ]);
        setSite(s.data);
        setRates(r.data);
        setMedia(m.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const heroImg = site?.hero_image_base64;
  const featuredPhotos = media.filter((p) => !p.locked).slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* HERO — "Meet Layla." heading ABOVE the picture */}
      <section className="relative min-h-screen flex flex-col items-center overflow-hidden pt-24 pb-16">
        {/* Ambient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-noir-base via-noir-surface to-noir-base" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/8793891/pexels-photo-8793891.jpeg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-noir-base/60 via-noir-base/30 to-noir-base" />
        <div className="noise-bg" />
        <Sparkles />

        <HeroReveal
          heroImg={heroImg}
          name={site?.name || "Layla"}
          subtitle={site?.hero_subtitle}
        />

        <div className="relative max-w-6xl mx-auto w-full px-6 sm:px-10 lg:px-16 flex flex-col items-center text-center">
          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 4.5 }}
            className="flex flex-col sm:flex-row gap-3 mb-14"
          >
            <Link
              to="/contact"
              className="group px-10 py-4 bg-pink-hot text-white text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all flex items-center justify-center gap-3"
              data-testid="hero-book-cta"
            >
              Reserve Your Evening
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/about"
              className="px-10 py-4 bg-transparent border border-pink-hot/40 text-champagne text-xs tracking-[0.3em] uppercase rounded-sm hover:border-pink-hot hover:bg-pink-hot/10 transition-all flex items-center justify-center"
              data-testid="hero-about-cta"
            >
              More about me
            </Link>
          </motion.div>

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 4.7 }}
            className="grid grid-cols-3 gap-8 sm:gap-16 max-w-lg"
          >
            {[
              { icon: Gem, label: "A Decade", sub: "At the top" },
              { icon: Heart, label: "99%", sub: "Become regulars" },
              { icon: Star, label: "Discreet", sub: "Always" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <s.icon className="w-5 h-5 text-pink-hot mx-auto mb-2" />
                <div className="font-display text-xl sm:text-2xl text-champagne leading-tight">{s.label}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-rosegold/80 mt-1">{s.sub}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured gallery strip */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-pink-blush mb-3">A private portrait</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-champagne leading-tight">
                Moments in <span className="italic text-pink-hot">silk</span>
              </h2>
            </div>
            <Link to="/gallery" className="text-xs tracking-[0.28em] uppercase text-pink-hot pink-underline-link" data-testid="view-full-gallery">
              View full gallery →
            </Link>
          </div>

          {featuredPhotos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {featuredPhotos.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className={`relative overflow-hidden rounded-sm ${i % 5 === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-[3/4]"}`}
                >
                  <img src={p.data_base64 || p.external_url} alt={p.caption || "featured"} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-noir-base/60 to-transparent" />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-champagne/50 italic font-display text-xl">New portraits coming soon.</div>
          )}
        </div>
      </section>

      {/* Rates teaser */}
      <section className="relative py-24 sm:py-32 bg-noir-surface/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-16">
            <div className="text-[10px] tracking-[0.35em] uppercase text-pink-blush mb-3">Investments</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-champagne">
              An <span className="italic text-pink-hot">experience</span> worth every moment
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {rates.slice(0, 4).map((r) => (
              <div key={r.id} className={`glass-panel rounded-lg p-8 relative ${r.highlight ? "border-pink-hot/50 glow-pink" : ""}`}>
                {r.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] tracking-[0.3em] uppercase bg-pink-hot text-white px-3 py-1 rounded-sm">
                    Most Requested
                  </div>
                )}
                <div className="text-[10px] tracking-[0.3em] uppercase text-rosegold mb-3">{r.duration}</div>
                <div className="font-display italic text-3xl text-champagne mb-4">{r.title}</div>
                <div className="font-display text-4xl text-pink-hot mb-2">${r.price}</div>
                <div className="text-xs text-champagne/60 mb-5">Deposit ${r.deposit}</div>
                <p className="text-sm text-champagne/70 leading-relaxed">{r.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/rates" className="inline-block px-8 py-3 border border-pink-hot/50 text-champagne text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-hot/10 hover:border-pink-hot transition-all">
              See all services
            </Link>
          </div>
        </div>
      </section>

      {/* Premium CTA */}
      <section className="relative py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/2732096/pexels-photo-2732096.jpeg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-noir-base/80 via-noir-base/90 to-noir-base" />
        <Sparkles />
        <div className="relative max-w-4xl mx-auto text-center px-6">
          <div className="text-[10px] tracking-[0.35em] uppercase text-pink-blush mb-4">Behind the velvet rope</div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-champagne leading-tight mb-6">
            Join the <span className="italic text-pink-hot text-glow">Diamond Circle</span>
          </h2>
          <p className="text-lg text-champagne/75 leading-relaxed max-w-2xl mx-auto mb-10">
            A private world of unreleased photos, intimate videos, and personal messages — for those who want more than a glimpse.
          </p>
          <Link
            to="/account"
            className="inline-block px-10 py-4 bg-pink-hot text-white text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all"
            data-testid="home-subscribe-cta"
          >
            Become a Member
          </Link>
        </div>
      </section>
    </div>
  );
}
